import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { createCampaign, addProspectToCampaign, setupWebhook } from '../../lib/emeliaService';

type Campaign = {
  id: string;
  name: string;
  template: string;
  created_at: string;
  status: string;
};

type Prospect = {
  id: string;
  email: string;
  prenom: string;
  nom: string;
};

const EmailCampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [newCampaign, setNewCampaign] = useState({
    name: '',
    template: ''
  });

  useEffect(() => {
    fetchCampaigns();
    fetchProspects();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const { data, error } = await supabase
        .from('email_campaigns')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCampaigns(data || []);
    } catch (err) {
      setError('Failed to fetch campaigns');
      console.error('Error fetching campaigns:', err);
    }
  };

  const fetchProspects = async () => {
    try {
      const { data, error } = await supabase
        .from('prospects')
        .select('id, email, prenom, nom, created_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProspects(data || []);
    } catch (err) {
      setError('Failed to fetch prospects');
      console.error('Error fetching prospects:', err);
    }
  };

  const handleCreateCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Create campaign in Emelia.io
      const campaign = await createCampaign(newCampaign.name, newCampaign.template);

      // Store campaign in database
      const { error } = await supabase
        .from('email_campaigns')
        .insert([{
          id: campaign.id,
          name: newCampaign.name,
          template: newCampaign.template,
          status: 'active'
        }]);

      if (error) throw error;

      setSuccess('Campaign created successfully!');
      setNewCampaign({ name: '', template: '' });
      fetchCampaigns();
    } catch (err) {
      setError('Failed to create campaign');
      console.error('Error creating campaign:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProspect = async (campaignId: string, prospectId: string) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const prospect = prospects.find(p => p.id === prospectId);
      if (!prospect) throw new Error('Prospect not found');

      await addProspectToCampaign(campaignId, {
        email: prospect.email,
        firstName: prospect.prenom,
        lastName: prospect.nom,
        company: "" // La propriété company n'existe pas dans la table prospects
      });

      setSuccess('Prospect added to campaign successfully!');
    } catch (err) {
      setError('Failed to add prospect to campaign');
      console.error('Error adding prospect to campaign:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigureWebhook = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await setupWebhook('all', `${window.location.origin}/api/webhooks/emelia`);
      setSuccess('Webhook configured successfully!');
    } catch (err) {
      setError('Failed to configure webhook');
      console.error('Error configuring webhook:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Email Campaigns</h1>

      {/* Error and Success Messages */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Create Campaign Form */}
      <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-8">
        <h2 className="text-xl font-semibold mb-4">Create New Campaign</h2>
        <form onSubmit={handleCreateCampaign}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Campaign Name
            </label>
            <input
              type="text"
              value={newCampaign.name}
              onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Email Template
            </label>
            <textarea
              value={newCampaign.template}
              onChange={(e) => setNewCampaign({ ...newCampaign, template: e.target.value })}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-32"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            {loading ? 'Creating...' : 'Create Campaign'}
          </button>
        </form>
      </div>

      {/* Campaigns List */}
      <div className="bg-white shadow-md rounded px-8 pt-6 pb-8">
        <h2 className="text-xl font-semibold mb-4">Active Campaigns</h2>
        {campaigns.length === 0 ? (
          <p className="text-gray-600">No campaigns found.</p>
        ) : (
          <div className="space-y-4">
            {campaigns.map((campaign) => (
              <div key={campaign.id} className="border rounded p-4">
                <h3 className="font-bold">{campaign.name}</h3>
                <p className="text-sm text-gray-600 mb-2">Status: {campaign.status}</p>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleConfigureWebhook()}
                    disabled={loading}
                    className="bg-green-500 hover:bg-green-700 text-white text-sm py-1 px-2 rounded"
                  >
                    Configure Webhook
                  </button>
                  <select
                    onChange={(e) => handleAddProspect(campaign.id, e.target.value)}
                    disabled={loading}
                    className="border rounded py-1 px-2 text-sm"
                    defaultValue=""
                  >
                    <option value="" disabled>Add Prospect</option>
                    {prospects.map((prospect) => (
                      <option key={prospect.id} value={prospect.id}>
                        {prospect.prenom} {prospect.nom}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailCampaignsPage;
